import React, { useState, useEffect, useCallback } from 'react';
import {
  Webhook, X, Loader2, Eye, EyeOff, Plus, Trash2,
  ToggleLeft, ToggleRight, Send, RefreshCw, ChevronRight,
  CheckCircle, XCircle, Copy, Check
} from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WebhookEndpoint {
  id: number;
  name: string;
  url: string;
  events: string[];
  is_enabled: boolean;
  has_secret: boolean;
  last_delivery: { success: boolean; created_at: string } | null;
}

interface Delivery {
  id: number;
  event: string;
  status_code: number | null;
  success: boolean;
  duration_ms: number | null;
  attempt_number: number;
  created_at: string;
  payload: Record<string, unknown>;
  response_body: string | null;
}

interface Props {
  agencyId: string;
  agencyName: string;
}

// ─── Event catalogue (label → slug) ──────────────────────────────────────────

const EVENT_OPTIONS: { label: string; slug: string; group: string }[] = [
  { group: 'Workspaces',     label: 'New workspace created',        slug: 'workspace.created' },
  { group: 'Workspaces',     label: 'Workspace deleted',            slug: 'workspace.deleted' },
  { group: 'Workspaces',     label: 'Workspace suspended',          slug: 'workspace.suspended' },
  { group: 'Workspaces',     label: 'Workspace reactivated',        slug: 'workspace.reactivated' },
  { group: 'Workspaces',     label: 'Workspace transfer complete',  slug: 'workspace.transfer_completed' },
  { group: 'Trials',         label: 'Trial started',                slug: 'workspace.trial_started' },
  { group: 'Trials',         label: 'Trial ending soon (3 days)',   slug: 'workspace.trial_expiring' },
  { group: 'Trials',         label: 'Trial expired',                slug: 'workspace.trial_expired' },
  { group: 'Subscriptions',  label: 'Subscription upgraded',        slug: 'subscription.upgraded' },
  { group: 'Subscriptions',  label: 'Subscription downgraded',      slug: 'subscription.downgraded' },
  { group: 'Subscriptions',  label: 'Subscription cancelled',       slug: 'subscription.cancelled' },
  { group: 'Subscriptions',  label: 'Subscription reactivated',     slug: 'subscription.reactivated' },
  { group: 'Subscriptions',  label: 'Payment failed',               slug: 'subscription.payment_failed' },
  { group: 'Users',          label: 'New user registered',          slug: 'user.registered' },
  { group: 'Users',          label: 'User deleted',                 slug: 'user.deleted' },
  { group: 'Team',           label: 'Team member invited',          slug: 'team.member_invited' },
  { group: 'Team',           label: 'Team member joined',           slug: 'team.member_joined' },
  { group: 'Team',           label: 'Team member removed',          slug: 'team.member_removed' },
  { group: 'Usage',          label: 'MAU approaching limit (80%)',            slug: 'workspace.mau_approaching' },
  { group: 'Usage',          label: 'MAU over limit',                         slug: 'workspace.mau_exceeded' },
  { group: 'Usage',          label: 'MAU critically over limit (125%)',       slug: 'workspace.mau_critical' },
  { group: 'Usage',          label: 'MAU persistently over limit (2 months)', slug: 'workspace.mau_persistent' },
];

const EVENT_GROUPS = EVENT_OPTIONS.map((e) => e.group).filter((g, i, arr) => arr.indexOf(g) === i);

// ─── Component ────────────────────────────────────────────────────────────────

const AgencyWebhooksTab: React.FC<Props> = ({ agencyId, agencyName }) => {
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [selected, setSelected] = useState<WebhookEndpoint | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Editor state
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editSecret, setEditSecret] = useState('');
  const [editEvents, setEditEvents] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // One-time secret reveal (on create or regenerate)
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  // Deliveries
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [expandedDelivery, setExpandedDelivery] = useState<number | null>(null);

  // Hover states
  const [hoveredWebhook, setHoveredWebhook] = useState<number | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

  useEffect(() => { loadWebhooks(); }, [agencyId]);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`/agencies/${agencyId}/webhooks`);
      setWebhooks(res.data.webhooks);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const selectWebhook = async (wh: WebhookEndpoint) => {
    setSelected(wh);
    setIsCreating(false);
    setEditName(wh.name);
    setEditUrl(wh.url);
    setEditSecret('');
    setEditEvents(wh.events);
    setRevealedSecret(null);
    loadDeliveries(wh.id);
  };

  const startCreate = () => {
    setSelected(null);
    setIsCreating(true);
    setEditName('');
    setEditUrl('');
    setEditSecret('');
    setEditEvents([]);
    setRevealedSecret(null);
    setDeliveries([]);
  };

  const loadDeliveries = async (webhookId: number) => {
    try {
      setLoadingDeliveries(true);
      const res = await apiService.get(`/agencies/${agencyId}/webhooks/${webhookId}/deliveries`);
      setDeliveries(res.data.deliveries);
    } catch {
      setDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleToggleEvent = (slug: string) => {
    setEditEvents((prev) =>
      prev.includes(slug) ? prev.filter((e) => e !== slug) : [...prev, slug]
    );
  };

  const handleSave = async () => {
    if (!editName.trim()) return showError('Name is required');
    if (!editUrl.trim()) return showError('URL is required');
    if (editEvents.length === 0) return showError('Select at least one event');

    setSaving(true);
    try {
      if (isCreating) {
        const res = await apiService.post(`/agencies/${agencyId}/webhooks`, {
          name: editName,
          url: editUrl,
          secret: editSecret || undefined,
          events: editEvents
        });
        if (res.data.secret_plaintext) {
          setRevealedSecret(res.data.secret_plaintext);
        }
        showSuccess('Webhook created');
      } else if (selected) {
        await apiService.put(`/agencies/${agencyId}/webhooks/${selected.id}`, {
          name: editName,
          url: editUrl,
          events: editEvents
        });
        showSuccess('Webhook updated');
      }
      await loadWebhooks();
      setIsCreating(false);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.name}"? This cannot be undone.`)) return;
    try {
      await apiService.delete(`/agencies/${agencyId}/webhooks/${selected.id}`);
      showSuccess('Webhook deleted');
      setSelected(null);
      setIsCreating(false);
      loadWebhooks();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete webhook');
    }
  };

  const handleToggle = async (wh: WebhookEndpoint, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiService.post(`/agencies/${agencyId}/webhooks/${wh.id}/toggle`);
      loadWebhooks();
      if (selected?.id === wh.id) {
        setSelected({ ...selected, is_enabled: !selected.is_enabled });
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to toggle webhook');
    }
  };

  const handleTest = async () => {
    if (!selected) return;
    setTesting(true);
    try {
      const res = await apiService.post(`/agencies/${agencyId}/webhooks/${selected.id}/test`);
      const allOk = res.data.results.every((r: any) => r.success);
      if (allOk) showSuccess('Test delivery succeeded');
      else showError('Test delivery failed — check the delivery log below');
      loadDeliveries(selected.id);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleRegenerateSecret = async () => {
    if (!selected) return;
    if (!window.confirm('Regenerate the signing secret? Your receiver will need to be updated.')) return;
    try {
      const res = await apiService.post(`/agencies/${agencyId}/webhooks/${selected.id}/regenerate-secret`);
      setRevealedSecret(res.data.secret_plaintext);
      setSelected({ ...selected, has_secret: true });
      showSuccess('Secret regenerated — copy it now');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to regenerate secret');
    }
  };

  const copySecret = async () => {
    if (!revealedSecret) return;
    await navigator.clipboard.writeText(revealedSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  // ─── Styles ────────────────────────────────────────────────────────────────

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: `1px solid ${focused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none'
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px'
  };

  const btnStyle = (hovered: boolean, variant: 'primary' | 'secondary' | 'danger' = 'primary'): React.CSSProperties => ({
    padding: '9px 16px',
    backgroundColor:
      variant === 'primary' ? (hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)')
      : variant === 'danger' ? (hovered ? 'var(--v2-error)' : 'var(--v2-error-bg)')
      : (hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)'),
    color:
      variant === 'primary' ? '#fff'
      : variant === 'danger' ? (hovered ? '#fff' : 'var(--v2-error)')
      : 'var(--v2-text)',
    border: variant === 'primary' ? 'none' : `1px solid ${variant === 'danger' ? 'var(--v2-error)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  });

  // ─── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
        <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '12px' }}>Loading webhooks...</p>
      </div>
    );
  }

  // ─── Layout ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>

      {/* ── Left: endpoint list ── */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--v2-border)',
          backgroundColor: 'var(--v2-surface-alt)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)' }}>
              Webhook Endpoints
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: 'var(--v2-text-muted)' }}>
              {webhooks.length} configured
            </p>
          </div>
          <button
            onClick={startCreate}
            onMouseEnter={() => setHoveredButton('add')}
            onMouseLeave={() => setHoveredButton(null)}
            style={btnStyle(hoveredButton === 'add', 'primary')}
          >
            <Plus size={14} />
            Add
          </button>
        </div>

        <div style={{ overflowY: 'auto', maxHeight: '520px' }}>
          {webhooks.length === 0 && !isCreating && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--v2-text-muted)', fontSize: '0.875rem' }}>
              No webhooks yet.<br />Click Add to configure one.
            </div>
          )}

          {webhooks.map((wh) => {
            const isActive = selected?.id === wh.id;
            return (
              <div
                key={wh.id}
                onClick={() => selectWebhook(wh)}
                onMouseEnter={() => setHoveredWebhook(wh.id)}
                onMouseLeave={() => setHoveredWebhook(null)}
                style={{
                  padding: '11px 14px',
                  borderBottom: '1px solid var(--v2-border)',
                  cursor: 'pointer',
                  backgroundColor: isActive
                    ? 'var(--v2-primary-light)'
                    : hoveredWebhook === wh.id ? 'var(--v2-surface-hover)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background-color 0.1s'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {/* Last delivery status dot */}
                    {wh.last_delivery && (
                      <span style={{
                        width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
                        backgroundColor: wh.last_delivery.success ? 'var(--v2-success)' : 'var(--v2-error)'
                      }} />
                    )}
                    <span style={{
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: isActive ? 'var(--v2-primary)' : 'var(--v2-text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {wh.name}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '0.6875rem',
                    color: 'var(--v2-text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginTop: '2px'
                  }}>
                    {wh.url.replace('https://', '')}
                  </div>
                </div>

                {/* Enable toggle */}
                <button
                  onClick={(e) => handleToggle(wh, e)}
                  title={wh.is_enabled ? 'Disable' : 'Enable'}
                  style={{
                    width: '32px', height: '18px',
                    borderRadius: '9px', border: 'none', marginLeft: '8px',
                    backgroundColor: wh.is_enabled ? 'var(--v2-success)' : 'var(--v2-border)',
                    cursor: 'pointer', position: 'relative', flexShrink: 0,
                    transition: 'background-color 0.2s'
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: wh.is_enabled ? '16px' : '2px',
                    width: '14px', height: '14px', borderRadius: '50%',
                    backgroundColor: '#fff', transition: 'left 0.2s',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Right: editor / empty state ── */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        overflow: 'hidden'
      }}>
        {!selected && !isCreating && (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <Webhook size={48} strokeWidth={1} color="var(--v2-text-muted)" />
            <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '16px' }}>
              Select an endpoint or click Add to create one.
            </p>
          </div>
        )}

        {(selected || isCreating) && (
          <>
            {/* Header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid var(--v2-border)',
              backgroundColor: 'var(--v2-surface-alt)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                {isCreating ? 'New Webhook Endpoint' : selected!.name}
              </h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!isCreating && selected && (
                  <>
                    <button
                      onClick={handleTest}
                      disabled={testing}
                      onMouseEnter={() => setHoveredButton('test')}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{ ...btnStyle(hoveredButton === 'test', 'secondary'), opacity: testing ? 0.6 : 1 }}
                    >
                      {testing
                        ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        : <Send size={14} />}
                      Test
                    </button>
                    <button
                      onClick={handleDelete}
                      onMouseEnter={() => setHoveredButton('del')}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={btnStyle(hoveredButton === 'del', 'danger')}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  onMouseEnter={() => setHoveredButton('save')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={{ ...btnStyle(hoveredButton === 'save', 'primary'), opacity: saving ? 0.6 : 1 }}
                >
                  {saving
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : null}
                  {isCreating ? 'Create' : 'Save'}
                </button>
              </div>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>

              {/* One-time secret banner */}
              {revealedSecret && (
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: 'var(--v2-warning-bg)',
                  border: '1px solid #f59e0b',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '20px'
                }}>
                  <p style={{ margin: '0 0 8px', fontSize: '0.8125rem', fontWeight: 600, color: '#92400e' }}>
                    ⚠️ Copy your signing secret now — it will not be shown again
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{
                      flex: 1, padding: '8px 10px',
                      backgroundColor: '#fffbeb',
                      border: '1px solid #f59e0b',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8125rem',
                      fontFamily: 'ui-monospace, monospace',
                      wordBreak: 'break-all'
                    }}>
                      {revealedSecret}
                    </code>
                    <button
                      onClick={copySecret}
                      style={{
                        padding: '8px 12px', border: '1px solid #f59e0b',
                        backgroundColor: secretCopied ? '#10b981' : '#fffbeb',
                        color: secretCopied ? '#fff' : '#92400e',
                        borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '0.8125rem', transition: 'all 0.15s'
                      }}
                    >
                      {secretCopied ? <Check size={14} /> : <Copy size={14} />}
                      {secretCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {/* Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder='e.g. "Pabbly Integration"'
                  onFocus={() => setFocusedInput('name')}
                  onBlur={() => setFocusedInput(null)}
                  style={inputStyle(focusedInput === 'name')}
                />
              </div>

              {/* URL */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Endpoint URL</label>
                <input
                  type="url"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  placeholder="https://connect.pabbly.com/workflow/..."
                  onFocus={() => setFocusedInput('url')}
                  onBlur={() => setFocusedInput(null)}
                  style={inputStyle(focusedInput === 'url')}
                />
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--v2-text-muted)' }}>
                  HTTPS required
                </p>
              </div>

              {/* Secret */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Signing Secret{' '}
                  <span style={{ fontWeight: 400, color: 'var(--v2-text-muted)' }}>(optional)</span>
                </label>

                {isCreating ? (
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={editSecret}
                      onChange={(e) => setEditSecret(e.target.value)}
                      placeholder="Leave blank to skip"
                      onFocus={() => setFocusedInput('secret')}
                      onBlur={() => setFocusedInput(null)}
                      style={{ ...inputStyle(focusedInput === 'secret'), paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      style={{
                        position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                        color: 'var(--v2-text-muted)'
                      }}
                    >
                      {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      flex: 1, padding: '10px 12px',
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem',
                      color: 'var(--v2-text-muted)',
                      fontFamily: 'ui-monospace, monospace'
                    }}>
                      {selected?.has_secret ? '••••••••••••••••' : 'No secret configured'}
                    </span>
                    <button
                      onClick={handleRegenerateSecret}
                      onMouseEnter={() => setHoveredButton('regen')}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={btnStyle(hoveredButton === 'regen', 'secondary')}
                    >
                      <RefreshCw size={14} />
                      {selected?.has_secret ? 'Regenerate' : 'Set Secret'}
                    </button>
                  </div>
                )}

                <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: 'var(--v2-text-muted)' }}>
                  If set, deliveries include <code>X-Webhook-Signature: sha256=…</code> for verification.
                </p>
              </div>

              {/* Events */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ ...labelStyle, marginBottom: '12px' }}>
                  Events to subscribe
                </label>

                {EVENT_GROUPS.map((group) => (
                  <div key={group} style={{ marginBottom: '14px' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {group}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {EVENT_OPTIONS.filter((e) => e.group === group).map((ev) => (
                        <label
                          key={ev.slug}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            cursor: 'pointer', fontSize: '0.875rem', color: 'var(--v2-text)'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editEvents.includes(ev.slug)}
                            onChange={() => handleToggleEvent(ev.slug)}
                            style={{ width: '16px', height: '16px', accentColor: 'var(--v2-primary)', cursor: 'pointer' }}
                          />
                          <span>{ev.label}</span>
                          <code style={{
                            fontSize: '0.6875rem',
                            color: 'var(--v2-text-muted)',
                            fontFamily: 'ui-monospace, monospace'
                          }}>
                            {ev.slug}
                          </code>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Delivery log (edit mode only) */}
              {!isCreating && selected && (
                <div>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                      Recent Deliveries
                    </h4>
                    <button
                      onClick={() => loadDeliveries(selected.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--v2-text-muted)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}
                    >
                      <RefreshCw size={12} /> Refresh
                    </button>
                  </div>

                  {loadingDeliveries ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
                    </div>
                  ) : deliveries.length === 0 ? (
                    <p style={{ color: 'var(--v2-text-muted)', fontSize: '0.875rem', textAlign: 'center', padding: '20px' }}>
                      No deliveries yet. Use Test to send a sample payload.
                    </p>
                  ) : (
                    <div style={{ border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                      {deliveries.map((d, i) => (
                        <div key={d.id}>
                          <div
                            onClick={() => setExpandedDelivery(expandedDelivery === d.id ? null : d.id)}
                            style={{
                              padding: '10px 14px',
                              borderBottom: i < deliveries.length - 1 ? '1px solid var(--v2-border)' : 'none',
                              display: 'flex', alignItems: 'center', gap: '10px',
                              cursor: 'pointer',
                              backgroundColor: expandedDelivery === d.id ? 'var(--v2-surface-alt)' : 'transparent',
                              transition: 'background-color 0.1s'
                            }}
                          >
                            {d.success
                              ? <CheckCircle size={15} color="var(--v2-success)" />
                              : <XCircle size={15} color="var(--v2-error)" />}
                            <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text)', flex: 1 }}>
                              {d.event}
                            </span>
                            {d.status_code && (
                              <span style={{
                                fontSize: '0.75rem', fontFamily: 'ui-monospace, monospace',
                                color: d.success ? 'var(--v2-success)' : 'var(--v2-error)'
                              }}>
                                {d.status_code}
                              </span>
                            )}
                            {d.duration_ms && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)' }}>
                                {d.duration_ms}ms
                              </span>
                            )}
                            <span style={{ fontSize: '0.6875rem', color: 'var(--v2-text-muted)' }}>
                              {new Date(d.created_at).toLocaleString()}
                            </span>
                            <ChevronRight
                              size={14}
                              color="var(--v2-text-muted)"
                              style={{ transform: expandedDelivery === d.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
                            />
                          </div>

                          {expandedDelivery === d.id && (
                            <div style={{ padding: '12px 14px', backgroundColor: 'var(--v2-surface-alt)', borderBottom: i < deliveries.length - 1 ? '1px solid var(--v2-border)' : 'none' }}>
                              <p style={{ margin: '0 0 6px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--v2-text-muted)' }}>PAYLOAD</p>
                              <pre style={{
                                margin: '0 0 10px', padding: '10px',
                                backgroundColor: 'var(--v2-surface)',
                                border: '1px solid var(--v2-border)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '0.6875rem',
                                overflowX: 'auto',
                                color: 'var(--v2-text)',
                                fontFamily: 'ui-monospace, monospace'
                              }}>
                                {JSON.stringify(d.payload, null, 2)}
                              </pre>
                              {d.response_body && (
                                <>
                                  <p style={{ margin: '0 0 6px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--v2-text-muted)' }}>RESPONSE</p>
                                  <pre style={{
                                    margin: 0, padding: '10px',
                                    backgroundColor: 'var(--v2-surface)',
                                    border: '1px solid var(--v2-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.6875rem',
                                    overflowX: 'auto',
                                    color: 'var(--v2-text)',
                                    fontFamily: 'ui-monospace, monospace'
                                  }}>
                                    {d.response_body}
                                  </pre>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AgencyWebhooksTab;
